import { IOrganisation } from "../../services/admin/admin.service";
import {
  TableRow,
  TableCell,
  Typography,
  Popover,
  Button,
  Icon,
} from "../../components/util";
import { PaginatedResults } from "../../components/content";
import * as Styled from "./styles.css";
import organisationService from "../../services/organisation/organisation.service";
import { useLocation } from "react-router-dom";
import { useContext, useMemo, useState } from "react";
import queryString from "query-string";
import Header from "../../components/layout/header";
import { OrgTypeChip } from "./OrgTypeChip";
import { PolicyOrgModal } from "./details/PolicyOrgModal";
import { useQueryClient } from "react-query";
import { AuthContext } from "../../contexts";
import { Role } from "../../types/user.type";

export type tOrgModalType = "Add" | "Update" | "Remove" | undefined;

type Props = {
  policy_id?: string;
  enableEditOption?: boolean;
};

const OrganisationsList = ({ policy_id, enableEditOption }: Props) => {
  const queryClient = useQueryClient();
  const { search } = useLocation();
  const urlParams: Record<string, any> = useMemo(
    () => queryString.parse(search),
    [search],
  );
  const user = useContext(AuthContext);
  const is_broker = useMemo(() => {
    if (user.user?.roles?.includes(Role.Broker)) {
      return true;
    } else {
      return false;
    }
  }, [user.user?.roles]);

  const searchTerm = useMemo(() => {
    return urlParams?.searchTerm ? urlParams?.searchTerm : "";
  }, [urlParams?.searchTerm]);

  const serviceParams = { policy_id };
  const [modalType, setModalType] = useState<tOrgModalType>();
  const [isModalVisible, setModalVisible] = useState<boolean>(false);
  const toggleModal = () => {
    setModalVisible(!isModalVisible);
    return;
  };

  const refetchQuery = `organisations-all${
    policy_id ? `-policy-${policy_id}` : ""
  }`;

  const OverflowMenuButtonOrganisation = () => {
    return (
      <>
        <Popover
          alignment="end"
          trigger={
            <Styled.OrgMoreOptionIconButton icon="kebab-horizontal"></Styled.OrgMoreOptionIconButton>
          }
        >
          <Styled.MoreOptionSelectMenu>
            <Styled.MoreOptionOrgSectionSelectOption>
              <Styled.MoreOptionButton
                variant="unstyled"
                type="button"
                onClick={() => {
                  setModalType("Update");
                  setModalVisible(!isModalVisible);
                  return;
                }}
              >
                Update
              </Styled.MoreOptionButton>
            </Styled.MoreOptionOrgSectionSelectOption>

            <Styled.MoreOptionOrgSectionSelectOption>
              <Styled.MoreOptionButton
                variant="unstyled"
                type="button"
                onClick={() => {
                  setModalType("Remove");
                  setModalVisible(!isModalVisible);
                  return;
                }}
              >
                Remove
              </Styled.MoreOptionButton>
            </Styled.MoreOptionOrgSectionSelectOption>
          </Styled.MoreOptionSelectMenu>
        </Popover>
      </>
    );
  };

  const ActionButton = (
    <>
      <Button
        onClick={() => {
          setModalType("Add");
          setModalVisible(!isModalVisible);
          return;
        }}
      >
        <Styled.AddOrgIcon>Add organisation </Styled.AddOrgIcon>
        <Icon icon="plus" />
      </Button>
      {isModalVisible && (
        <PolicyOrgModal
          data={{ policy_id: policy_id }}
          onClose={toggleModal}
          onConfirm={() => queryClient.invalidateQueries(refetchQuery)}
          modalType={modalType}
        />
      )}
    </>
  );

  const organisationsRow = (orgsSlice: IOrganisation[]) => {
    return (
      <>
        {orgsSlice.map((org: IOrganisation, i: number) => (
          <TableRow key={org.id}>
            <TableCell expand={true}>
              {is_broker ? (
                <Typography
                  styled={{ text: { case: "title", weight: "heavy" } }}
                  truncate={true}
                >
                  {org.name}
                </Typography>
              ) : (
                <Styled.Link
                  href={`/organisations/${org.id}${location.search || ""}`}
                  router
                  title={org.name}
                >
                  {org.name}
                </Styled.Link>
              )}
            </TableCell>
            <TableCell expand={true}>
              <Typography styled={{ text: { case: "title" } }} truncate={true}>
                {!!org.industry?.occupation && <>{org.industry.occupation}, </>}
                {org.industry?.name}
              </Typography>
            </TableCell>
            <TableCell justifyContent="end">
              <Typography styled={{ text: { align: "right" } }}>
                {org.associated_policies?.length || 0}
              </Typography>
            </TableCell>
            <TableCell justifyContent="end">
              <Typography styled={{ text: { align: "right" } }}>
                {org.users?.length || 0}
              </Typography>
            </TableCell>
            {!is_broker && (
              <TableCell>
                <OrgTypeChip org_type={org.type} />
              </TableCell>
            )}
            {!is_broker && enableEditOption ? (
              <>
                <TableCell>
                  <OverflowMenuButtonOrganisation />
                  {isModalVisible && (
                    <PolicyOrgModal
                      data={{
                        policy_id: policy_id,
                        org_id: org.id,
                        org_name: org.name,
                      }}
                      onClose={toggleModal}
                      onConfirm={() =>
                        queryClient.invalidateQueries(refetchQuery)
                      }
                      modalType={modalType}
                    />
                  )}
                </TableCell>
              </>
            ) : (
              ""
            )}
          </TableRow>
        ))}
      </>
    );
  };

  const tableColumns = useMemo(() => {
    const cols = [
      { title: "Name", expand: true },
      { title: "Industry" },
      { title: "Policies", collapse: !is_broker },
      { title: "Users", collapse: !is_broker },
    ];

    if (!is_broker) {
      cols.push({ title: "Type", collapse: true });
    }

    if (enableEditOption && !is_broker) {
      cols.push({ title: "", collapse: true });
    }
    return cols;
  }, [enableEditOption, is_broker]);

  return (
    <>
      <PaginatedResults
        id={refetchQuery}
        displayMiscButton={
          enableEditOption &&
          ((actButton: boolean) => (
            <Header
              title="Organisation"
              type="section"
              actions={actButton && ActionButton}
            />
          ))
        }
        searchTerm={searchTerm}
        tableColumns={tableColumns}
        service={organisationService.getAllOrganisations}
        searchService={organisationService.getAllOrganisationsBySearchTerm}
        serviceParams={serviceParams}
        tableRows={organisationsRow}
        missing={{
          icon: "organization",
          type: "organisations",
          message: "This is where we’ll show all Organisations.",
        }}
      />
    </>
  );
};

export default OrganisationsList;
